// Globals
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isFQDN } from 'validator';

// UI
import {
  Button,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  Tooltip,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { FormListFieldData } from 'antd/lib/form/FormList';

// Locals
import useGiideOperations from '../../hooks/useGiideOperations';
import { IState } from '../../graphql/types';
import { setUpgradeModalOpen } from '../../actions';
import styles from './GiideWithStep.module.css';
import { tealButtonTheme } from '../../theme';

const QrCodeIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.96424 2.68571C10.0668 2.42931 9.94209 2.13833 9.6857 2.03577C9.4293 1.93322 9.13832 2.05792 9.03576 2.31432L5.03576 12.3143C4.9332 12.5707 5.05791 12.8617 5.3143 12.9642C5.5707 13.0668 5.86168 12.9421 5.96424 12.6857L9.96424 2.68571ZM3.85355 5.14646C4.04882 5.34172 4.04882 5.6583 3.85355 5.85356L2.20711 7.50001L3.85355 9.14646C4.04882 9.34172 4.04882 9.6583 3.85355 9.85356C3.65829 10.0488 3.34171 10.0488 3.14645 9.85356L1.14645 7.85356C0.951184 7.6583 0.951184 7.34172 1.14645 7.14646L3.14645 5.14646C3.34171 4.9512 3.65829 4.9512 3.85355 5.14646ZM11.1464 5.14646C11.3417 4.9512 11.6583 4.9512 11.8536 5.14646L13.8536 7.14646C14.0488 7.34172 14.0488 7.6583 13.8536 7.85356L11.8536 9.85356C11.6583 10.0488 11.3417 10.0488 11.1464 9.85356C10.9512 9.6583 10.9512 9.34172 11.1464 9.14646L12.7929 7.50001L11.1464 5.85356C10.9512 5.6583 10.9512 5.34172 11.1464 5.14646Z"
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
      ></path>
    </svg>
  );
};

interface IProps {
  giideId: string;
  giideEmbedCodeState?: string | null;
  giideEmbedCodeAllowedDomains?: (string | null)[] | null;
}

const ButtonAddons: React.FC<{
  length: number;
  remove: (index: number | number[]) => void;
  add: () => void;
  onChange: () => void;
  index: number;
  field: FormListFieldData;
}> = ({ length, remove, add, onChange, index, field }) => {
  let Buttons = (
    <Flex align={'center'} gap={'small'}>
      <Tooltip title={'Remove this domain'}>
        <Button
          onClick={() => {
            onChange();
            remove(field.name);
          }}
          icon={<MinusOutlined />}
        ></Button>
      </Tooltip>
      <Tooltip title={'Add another domain'}>
        <Button
          onClick={() => {
            onChange();
            add();
          }}
          icon={<PlusOutlined />}
        ></Button>
      </Tooltip>
    </Flex>
  );

  if ((index === 0 && length > 1) || (index + 1 === length && length === 5)) {
    Buttons = (
      <Tooltip title={'Remove this domain'}>
        <Button
          onClick={() => {
            onChange();
            remove(field.name);
          }}
          icon={<MinusOutlined />}
        ></Button>
      </Tooltip>
    );
  } else if (index === 0 && length === 1) {
    Buttons = (
      <Tooltip title={'Add another domain'}>
        <Button
          onClick={() => {
            onChange();
            add();
          }}
          icon={<PlusOutlined />}
        ></Button>
      </Tooltip>
    );
  }

  return <div style={{ width: 'fit-content' }}>{Buttons}</div>;
};

export const EmbedCode: React.FC<IProps> = ({
  giideId,
  giideEmbedCodeState,
  giideEmbedCodeAllowedDomains,
}) => {
  const dispatch = useDispatch();

  const [copiedEmbedMessage, setCopiedEmbedMessage] = useState<string>('');
  const [EmbedCodeModalVisible, setEmbedCodeModalVisible] =
    useState<boolean>(false);
  const [EmbedCodeURLInputsVisible, setEmbedCodeURLInputsVisible] =
    useState<boolean>(giideEmbedCodeState === 'byDomainOnly');
  const [EmbedCodeInitialValues, setEmbedCodeInitialValues] = useState<{
    embedCodeState: string;
    embedCodeAllowedDomains: (string | null)[];
  }>({
    embedCodeState: giideEmbedCodeState ?? 'none',
    embedCodeAllowedDomains: (giideEmbedCodeAllowedDomains &&
    giideEmbedCodeAllowedDomains.length > 0
      ? giideEmbedCodeAllowedDomains
      : ['']
    ).map(domain => domain?.replace('https://', '') || ''),
  });

  useEffect(() => {
    if (EmbedCodeModalVisible) {
      // reset initial values
      setEmbedCodeInitialValues({
        embedCodeState: giideEmbedCodeState ?? 'none',
        embedCodeAllowedDomains: (giideEmbedCodeAllowedDomains &&
        giideEmbedCodeAllowedDomains.length > 0
          ? giideEmbedCodeAllowedDomains
          : ['']
        ).map(domain => domain?.replace('https://', '') || ''),
      });
    }
  }, [
    EmbedCodeModalVisible,
    giideEmbedCodeState,
    giideEmbedCodeAllowedDomains,
  ]);

  const { updateGiideEmbedCode, giideEmbedCodeEditing } =
    useGiideOperations(giideId);
  const [form] = Form.useForm();

  const giideAllowEmbedCode = useSelector<IState, boolean>(
    state =>
      (state.user?.plan?.features?.embedCode?.isActive ?? false) &&
      (state.user?.plan?.features?.embedCode?.allowEmbedCode ?? false)
  );
  const giideEmbedCodeUpgrade = (
    <Flex justify={'center'} align={'center'} gap={'small'}>
      <span className={styles.shareEmbedPopoverWrapperText}>
        Embed codes are available for users on the Business plan.
      </span>
      <Button
        type={'default'}
        onClick={() => dispatch(setUpgradeModalOpen(true))}
      >
        Upgrade
      </Button>
    </Flex>
  );

  const [isDataChanged, setIsDataChanged] = useState(false);

  const handleEmbedCodeFormValuesChange = useCallback(
    changedValues => {
      // change visibility only on embedCodeState change
      if (changedValues.embedCodeState) {
        if (changedValues.embedCodeState === 'byDomainOnly') {
          setEmbedCodeURLInputsVisible(true);
        } else {
          setEmbedCodeURLInputsVisible(false);
        }

        if (
          EmbedCodeInitialValues.embedCodeState !== changedValues.embedCodeState
        ) {
          // reset values on embed code change
          setEmbedCodeInitialValues({
            embedCodeState: changedValues.embedCodeState,
            embedCodeAllowedDomains: [''],
          });
          setIsDataChanged(true);
        }
      }
    },
    [EmbedCodeInitialValues.embedCodeState]
  );

  useEffect(() => {
    if (EmbedCodeModalVisible) {
      form.resetFields();
      handleEmbedCodeFormValuesChange({
        embedCodeState: EmbedCodeInitialValues.embedCodeState,
      });
    } else {
      setIsDataChanged(false);
    }
  }, [
    form,
    EmbedCodeModalVisible,
    EmbedCodeInitialValues,
    handleEmbedCodeFormValuesChange,
  ]);

  const giideEmbedCode = useMemo<string>(
    () =>
      `<iframe width="480" height="480" frameborder="0" src="${process.env.REACT_APP_PREVIEW_URL}giide-${giideId}?embed=1"></iframe>`,
    [giideId]
  );

  const copyEmbedToClipboard = useCallback(() => {
    navigator.clipboard.writeText(giideEmbedCode);
    setCopiedEmbedMessage('Copied to clipboard!');
    setTimeout(function () {
      setCopiedEmbedMessage('');
    }, 1000);
  }, [giideEmbedCode]);

  const handleSaveEmbed = useCallback(
    async values => {
      const domains = Array.from<string>(
        new Set(values.embedCodeAllowedDomains || [])
      );
      updateGiideEmbedCode(values.embedCodeState, domains, true);
      setIsDataChanged(false);
      setEmbedCodeInitialValues({
        embedCodeState: values.embedCodeState,
        embedCodeAllowedDomains: domains,
      });
    },
    [updateGiideEmbedCode]
  );

  return (
    <>
      <Modal
        title={'Embed giide'}
        open={EmbedCodeModalVisible}
        onCancel={() => setEmbedCodeModalVisible(false)}
        footer={null}
        forceRender
      >
        <Divider className={styles.shareEmbedCodeDivider} />
        <TextArea
          readOnly
          className={styles.shareEmbedCodeTextArea}
          value={giideEmbedCode}
        />
        <Divider className={styles.shareEmbedCodeDivider} />
        <div>
          <Tooltip
            placement="topRight"
            open={!!copiedEmbedMessage}
            title={copiedEmbedMessage}
          >
            <Button
              block={true}
              type={'primary'}
              className={styles.shareEmbedCodeCopyBtn}
              onClick={copyEmbedToClipboard}
            >
              Copy Embed Code
            </Button>
          </Tooltip>
        </div>

        <Form
          onFinish={handleSaveEmbed}
          form={form}
          onValuesChange={handleEmbedCodeFormValuesChange}
          layout={'vertical'}
          initialValues={{
            ...EmbedCodeInitialValues,
          }}
        >
          <div className={styles.shareEmbedCodeText}>
            <Form.Item
              name="embedCodeState"
              label={'Where can this giide be embedded?'}
            >
              <Select>
                <Select.Option value="none">Nowhere</Select.Option>
                <Select.Option value="all">Anywhere</Select.Option>
                <Select.Option value="byDomainOnly">
                  Specific domains only
                </Select.Option>
              </Select>
            </Form.Item>
          </div>
          <div className={styles.shareEmbedCodeText}>
            {EmbedCodeURLInputsVisible && (
              <Form.List name="embedCodeAllowedDomains">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Form.Item required={false} key={field.key}>
                        <Form.Item
                          {...field}
                          noStyle
                          rules={[
                            {
                              required: true,
                              validator: async (_, value) => {
                                if (isFQDN(value)) {
                                  return Promise.resolve();
                                } else {
                                  return Promise.reject(
                                    new Error(
                                      'Please set valid URL or remove field.'
                                    )
                                  );
                                }
                              },
                              message: 'Please set valid URL or remove field.',
                            },
                          ]}
                        >
                          <Flex gap={'small'} align={'center'}>
                            <Input
                              addonBefore="https://"
                              onInput={() => setIsDataChanged(true)}
                              placeholder="www.url.com"
                            />
                            <ButtonAddons
                              index={index}
                              onChange={() => setIsDataChanged(true)}
                              add={add}
                              remove={remove}
                              field={field}
                              length={fields.length}
                            />
                          </Flex>
                        </Form.Item>
                      </Form.Item>
                    ))}
                  </>
                )}
              </Form.List>
            )}
          </div>
          <Form.Item>
            <ConfigProvider theme={tealButtonTheme}>
              <Button
                block={true}
                type={'primary'}
                disabled={!isDataChanged && !giideEmbedCodeEditing}
                htmlType="submit"
                loading={giideEmbedCodeEditing}
              >
                Save
              </Button>
            </ConfigProvider>
          </Form.Item>
        </Form>
      </Modal>

      <Popover
        content={giideEmbedCodeUpgrade}
        overlayClassName={
          giideAllowEmbedCode ? styles.shareEmbedPopoverDisabledWrapper : ''
        }
      >
        <Flex
          align={'center'}
          className={
            giideAllowEmbedCode
              ? styles.shareEmbedCodeWrapper
              : styles.shareEmbedCodeDisabledWrapper
          }
          onClick={() =>
            giideAllowEmbedCode ? setEmbedCodeModalVisible(true) : null
          }
        >
          <QrCodeIcon />
        </Flex>
      </Popover>
    </>
  );
};
